import { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { Mail, RefreshCw, CheckCircle2, AlertCircle, ArrowLeft, Loader2 } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuthStore } from '../../stores/auth';

interface VerifyOtpResponse {
  success: boolean;
  message?: string;
  token?: string;
  user?: {
    id: string;
    email: string;
    fullName: string;
  };
  expired?: boolean;
}

export function CheckEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const email = searchParams.get('email') || '';
  const { setTokenAndFetchUser } = useAuthStore();
  
  // OTP state
  const [otp, setOtp] = useState(['', '', '', '']);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState('');
  const [verifySuccess, setVerifySuccess] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  
  // Resend state
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState('');
  const [cooldown, setCooldown] = useState(0);
  
  // Cooldown timer
  useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldown]);
  
  // Auto-focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);
  
  // Handle OTP input change
  const handleOtpChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setVerifyError('');
    
    // Auto-focus next input
    if (value && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }
    
    // Auto-submit when all digits are entered
    if (value && index === 3 && newOtp.every(d => d !== '')) {
      verifyOtp(newOtp.join(''));
    }
  };
  
  // Handle key down for backspace navigation
  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'Enter') {
      const otpCode = otp.join('');
      if (otpCode.length === 4) {
        verifyOtp(otpCode);
      }
    }
  };
  
  // Handle paste
  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
    if (pastedData.length === 4) {
      const newOtp = pastedData.split('');
      setOtp(newOtp);
      inputRefs.current[3]?.focus();
      verifyOtp(pastedData);
    }
  };
  
  // Verify OTP
  const verifyOtp = async (otpCode: string) => {
    if (!email || otpCode.length !== 4) return;
    
    setIsVerifying(true);
    setVerifyError('');
    
    try {
      const response = await api.post<VerifyOtpResponse>('/auth/verify-otp', {
        email,
        otp: otpCode,
      });
      
      if (response.success && response.token) {
        setVerifySuccess(true);
        
        // Set token and fetch user in one atomic operation
        await setTokenAndFetchUser(response.token);
        
        // Redirect to dashboard after brief delay
        setTimeout(() => {
          navigate('/app');
        }, 1000);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Invalid code. Please try again.';
      setVerifyError(message);
      // Clear OTP and refocus first input
      setOtp(['', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setIsVerifying(false);
    }
  };
  
  const handleResend = async () => {
    if (cooldown > 0 || !email) return;
    
    setIsResending(true);
    setResendError('');
    setResendSuccess(false);
    
    try {
      await api.post('/auth/resend-verification', { email });
      setResendSuccess(true);
      setCooldown(60); // 60 second cooldown
      // Clear OTP fields
      setOtp(['', '', '', '']);
      setVerifyError('');
      inputRefs.current[0]?.focus();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to send email';
      setResendError(message);
    } finally {
      setIsResending(false);
    }
  };
  
  if (verifySuccess) {
    return (
      <div className="w-full max-w-md">
        <div className="bg-neutral-0 dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 shadow-sm p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-success-100 dark:bg-success-900/30 flex items-center justify-center">
            <CheckCircle2 className="w-8 h-8 text-success-600 dark:text-success-400" />
          </div>
          <h2 className="text-xl font-medium text-neutral-900 dark:text-neutral-100 mb-2">
            Email verified!
          </h2>
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            Redirecting you to the dashboard...
          </p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="w-full max-w-md">
      <div className="bg-neutral-0 dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 shadow-sm p-8 text-center">
        {/* Email Icon */}
        <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-accent-100 dark:bg-accent-900/30 flex items-center justify-center">
          <Mail className="w-8 h-8 text-accent-600 dark:text-accent-400" />
        </div>
        
        <h2 className="text-xl font-medium text-neutral-900 dark:text-neutral-100 mb-2">
          Check your email
        </h2>
        
        <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-6">
          We sent a verification code to{' '}
          <span className="font-medium text-neutral-900 dark:text-neutral-100">{email || 'your email'}</span>
        </p>
        
        {/* OTP Input */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-3">
            Enter 4-digit code
          </label>
          <div className="flex justify-center gap-3">
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={(el) => { inputRefs.current[index] = el }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={index === 0 ? handlePaste : undefined}
                disabled={isVerifying}
                className="w-14 h-14 text-center text-2xl font-semibold rounded-lg border-2 border-neutral-300 dark:border-neutral-600 bg-neutral-0 dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20 outline-none transition-all disabled:opacity-50"
                autoComplete="one-time-code"
              />
            ))}
          </div>
          
          {/* Loading indicator */}
          {isVerifying && (
            <div className="mt-4 flex items-center justify-center gap-2 text-sm text-neutral-600 dark:text-neutral-400">
              <Loader2 className="w-4 h-4 animate-spin" />
              Verifying...
            </div>
          )}
        </div>
        
        {/* Error message */}
        {verifyError && (
          <div className="mb-4 px-4 py-3 rounded-md text-sm flex items-center gap-2 bg-error-50 dark:bg-error-900/20 text-error-600 dark:text-error-400 border border-error-500/20">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {verifyError}
          </div>
        )}
        
        {/* Success message */}
        {resendSuccess && (
          <div className="mb-4 px-4 py-3 rounded-md text-sm flex items-center gap-2 bg-success-50 dark:bg-success-900/20 text-success-600 dark:text-success-400 border border-success-500/20">
            <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
            New code sent successfully!
          </div>
        )}
        
        {/* Resend error */}
        {resendError && (
          <div className="mb-4 px-4 py-3 rounded-md text-sm flex items-center gap-2 bg-error-50 dark:bg-error-900/20 text-error-600 dark:text-error-400 border border-error-500/20">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {resendError}
          </div>
        )}
        
        {/* Instructions */}
        <div className="bg-neutral-50 dark:bg-neutral-700/50 rounded-lg p-4 mb-6 text-left">
          <p className="text-sm text-neutral-700 dark:text-neutral-300 mb-2">
            Check your <strong>Inbox</strong> and <strong>Spam</strong> folders.
          </p>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            The code expires in 15 minutes.
          </p>
        </div>
        
        {/* Resend button */}
        <div className="mb-6">
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-3">
            Didn't receive the code?
          </p>
          <button
            onClick={handleResend}
            disabled={isResending || cooldown > 0 || !email}
            className="btn btn-secondary inline-flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${isResending ? 'animate-spin' : ''}`} />
            {cooldown > 0 
              ? `Resend code (${cooldown}s)` 
              : isResending 
                ? 'Sending...' 
                : 'Resend code'
            }
          </button>
        </div>
        
        <div className="h-px bg-neutral-200 dark:bg-neutral-700 my-6" />
        
        {/* Back to login */}
        <Link 
          to="/login" 
          className="inline-flex items-center gap-2 text-sm text-accent-600 hover:text-accent-700 dark:text-accent-400 dark:hover:text-accent-300 font-medium"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to sign in
        </Link>
      </div>
    </div>
  );
}
